import React from 'react';
import { CircleCheckIcon, CircleAlertIcon, CircleSlashIcon, ShieldCheckIcon } from 'lucide-react';
import { Band, PullQuote } from './Band';
import { candidateReflection, decisionSummary, decisionSummaryException, defectResults, observedPrimary, observedSupporting, reconstruction, reconstructionAttestation } from '../../data/site';
const OUTCOME_STYLES: Record<string, {
  icon: typeof CircleCheckIcon;
  className: string;
}> = {
  'Handled · resolved correctly': {
    icon: CircleCheckIcon,
    className: 'text-teal'
  },
  'Handled · appropriately escalated': {
    icon: CircleAlertIcon,
    className: 'text-teal'
  },
  'Verified proportionately': {
    icon: ShieldCheckIcon,
    className: 'text-teal'
  },
  Missed: {
    icon: CircleSlashIcon,
    className: 'text-clay'
  }
};
const evidenceKey = [{
  label: 'Handled and correctly resolved',
  credit: 'Full',
  filled: true
}, {
  label: 'Clean case, verified proportionately',
  credit: 'Full',
  filled: true
}, {
  label: 'Handled and appropriately escalated',
  credit: 'Near-full',
  filled: true
}, {
  label: 'Over-checked, or resolved incorrectly',
  credit: 'Partial',
  filled: false
}, {
  label: 'Missed',
  credit: 'None',
  filled: false
}];
const reviewerFields = ['Material concerns', 'Evidence relied on', 'Reviewer', 'Date'];
const FIELD_LABEL = 'font-mono text-[11px] uppercase tracking-[0.1em] text-graphite';
const SECTION_LABEL = 'font-mono text-xs uppercase tracking-[0.14em] text-teal';
export function DecisionRecord() {
  return <Band id="record" eyebrow="What you get back" title="Not a percentile. A two-page record you can hand to your GC." description="Every line traces to one action at one timestamp. No decision is recorded until a named person signs off on the evidence, not the summary." aside={<PullQuote data-id="element-79" data-mp="1ef5s7idb6h66">
          A score tells you who ranked higher. This tells you how each person
          exercised judgement when the AI was involved.
        </PullQuote>} data-id="element-78" data-mp="qq4epgpmcfu1">
      <div className="grid gap-6 lg:grid-cols-[1.4fr_0.6fr] lg:items-start" data-id="element-80" data-mp="736bgkmzj700">
        <article className="overflow-hidden rounded-2xl border border-mist bg-white shadow-[0_30px_70px_-50px_rgba(14,36,54,0.6)]" data-id="element-81" data-mp="713h5oesurph">
          <header className="flex flex-wrap items-center justify-between gap-3 border-b border-mist px-7 py-5" data-id="element-82" data-mp="g3cok5eru4yn">
            <div data-id="element-83" data-mp="kuuocq525zi1">
              <p className="font-mono text-xs uppercase tracking-[0.14em] text-graphite" data-id="element-84" data-mp="kvctkc9p7o23">
                Decision record · candidate 0417
              </p>
              <h3 className="mt-1.5 font-display text-2xl text-ink" data-id="element-85" data-mp="usiknkibbf2b">
                Financial Controller · Round 1
              </h3>
            </div>
            <p className="font-mono text-xs text-graphite" data-id="element-86" data-mp="yuvp85lbp6xi">
              library v2026.03.4 · 6 scenarios
            </p>
          </header>

          {/* Decision summary — machine-generated description */}
          <div className="border-b border-mist px-7 py-6" data-id="element-87" data-mp="30njvyox6eci1">
            <h4 className={SECTION_LABEL} data-id="element-88" data-mp="35k0mtqvrf4yt">Decision summary</h4>
            <p className="mt-4 max-w-[76ch] border-l-2 border-clay pl-5 text-[17px] font-medium leading-relaxed text-ink" data-id="element-89" data-mp="2y1at17pflikm">
              {decisionSummaryException}
            </p>
            <p className="mt-4 max-w-[76ch] text-[17px] leading-relaxed text-graphite" data-id="element-90" data-mp="2t8hnwiq1w880">
              {decisionSummary}
            </p>
          </div>

          <div className="px-7 py-6" data-id="element-91" data-mp="2jd69k1ik130s">
            <h4 className={SECTION_LABEL} data-id="element-92" data-mp="2je7uqytab5w8">Scenario schedule · 6 of 6</h4>
            <ul className="mt-4 divide-y divide-mist/70" data-id="element-93" data-mp="3gz3tm3cl1geu">
              {defectResults.map(result => {
              const style = OUTCOME_STYLES[result.outcome];
              const Icon = style.icon;
              const missed = result.outcome === 'Missed';
              return <li key={result.id} className={`flex gap-4 py-4 ${missed ? '-mx-4 rounded-lg bg-clay/[0.06] px-4' : ''}`} data-id="element-94" data-mp="32zcdnx2i9py7">
                    <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${style.className}`} aria-hidden="true" data-id="element-95" data-mp="1rjxh2ue7l03m" />
                    <div className="flex-1" data-id="element-96" data-mp="3k9onfhzekjf5">
                      <div className="flex flex-wrap items-baseline justify-between gap-2" data-id="element-97" data-mp="3kag9o27414sr">
                        <span className="flex flex-wrap items-baseline gap-3" data-id="element-98" data-mp="35hnx5jn1qa4v">
                          <span className="font-mono text-sm text-ink" data-id="element-99" data-mp="3ajd68tqpkdqb">{result.id}</span>
                          <span className="font-mono text-[11px] uppercase tracking-[0.1em] text-muted" data-id="element-100" data-mp="24bnnwdg5c2p">
                            {result.type}
                          </span>
                        </span>
                        <span className={`text-sm font-semibold ${style.className}`} data-id="element-101" data-mp="3p76m8fjphi9f">
                          {result.outcome} · {result.credit}
                        </span>
                      </div>
                      <p className="mt-1.5 text-[15px] leading-relaxed text-graphite" data-id="element-102" data-mp="2dr9lzv6f3bww">
                        {result.detail}
                      </p>
                      {result.reconstructed ? <p className="mt-2 font-mono text-[11px] text-clay" data-id="element-103" data-mp="2a8sq57dtg49x">
                          → reconstructed below
                        </p> : null}
                    </div>
                  </li>;
            })}
            </ul>
          </div>

          {/* Reconstruction */}
          <div className="border-t border-mist bg-bone/60 px-7 py-6" data-id="element-104" data-mp="1rk5l2ovn3ewc">
            <h4 className={SECTION_LABEL} data-id="element-105" data-mp="1rkfb2cgopm21">Reconstruction · 00:14:31</h4>
            <dl className="mt-4 divide-y divide-mist/70" data-id="element-106" data-mp="1mmdt5uuzvss5">
              {reconstruction.map(row => <div key={row.field} className="grid gap-1 py-3 sm:grid-cols-[16rem_1fr] sm:gap-6" data-id="element-107" data-mp="21aqrgyhk1npt">
                  <dt className={FIELD_LABEL} data-id="element-108" data-mp="18tee1tz4k0l4">{row.field}</dt>
                  <dd className={`text-[15px] leading-snug ${row.value === 'Missed' || row.value === 'Incorrect' ? 'font-semibold text-clay' : 'text-ink'}`} data-id="element-109" data-mp="18rtlub0a8tqf">
                    {row.value}
                  </dd>
                </div>)}
            </dl>
            <p className="mt-5 max-w-[70ch] text-sm leading-relaxed text-muted" data-id="element-110" data-mp="22bsm9sadrv6s">
              Confidence is recorded at submission, not during the work, so that
              being asked does not itself prompt the verification.
            </p>

            <dl className="mt-4 rounded-xl border border-mist bg-white px-5 py-4" data-id="element-111" data-mp="1ekwngb7ibzq1">
              {reconstructionAttestation.map(row => <div key={row.field} className="grid gap-1 py-2 sm:grid-cols-[14rem_1fr] sm:gap-6" data-id="element-112" data-mp="1jiy6wx8ui121">
                  <dt className={FIELD_LABEL} data-id="element-113" data-mp="2uzy5ubpp7e51">{row.field}</dt>
                  <dd className={`text-[15px] leading-snug ${row.adverse ? 'font-semibold text-clay' : 'text-ink'}`} data-id="element-114" data-mp="2v1sjd4yn43z3">
                    {row.value}
                  </dd>
                </div>)}
            </dl>
          </div>

          {/* Candidate reflection */}
          <div className="border-t border-mist px-7 py-6" data-id="element-115" data-mp="ekkujc4ql579">
            <h4 className={SECTION_LABEL} data-id="element-116" data-mp="9mj9ilnr2vt5">Candidate reflection</h4>
            <blockquote className="mt-4 border-l-2 border-mist pl-5" data-id="element-117" data-mp="9m9jiy2pgong">
              <p className="max-w-[72ch] text-[17px] leading-relaxed text-ink" data-id="element-118" data-mp="5lzatdnj3k4w">
                “{candidateReflection}”
              </p>
            </blockquote>
            <p className="mt-4 text-sm leading-relaxed text-muted" data-id="element-119" data-mp="3rrwagalg0976">
              Recorded before results were known. Candidates frequently identify
              the failure they did not catch.
            </p>
          </div>

          {/* Observed behaviour */}
          <div className="border-t border-mist px-7 py-6" data-id="element-120" data-mp="1pzzzb236t1cx">
            <h4 className={SECTION_LABEL} data-id="element-121" data-mp="1l1gdr2si9rl3">Observed behaviour</h4>

            <div className="mt-4 flex flex-wrap items-baseline justify-between gap-4 border-b border-mist pb-4" data-id="element-122" data-mp="twcbs1hiejn3">
              <p className="text-lg text-ink" data-id="element-123" data-mp="yv5o3ql284lo">{observedPrimary.label}</p>
              <p className="font-mono text-xl text-ink" data-id="element-124" data-mp="yx9tkcylxbo2">{observedPrimary.value}</p>
            </div>

            <dl className="mt-4 grid gap-x-8 gap-y-3 sm:grid-cols-2" data-id="element-125" data-mp="l27p7u9erudd">
              {observedSupporting.map(obs => <div key={obs.label} className="flex items-baseline justify-between gap-4 border-b border-mist/70 pb-2" data-id="element-126" data-mp="b64cjugxkdc6">
                  <dt className="text-[15px] text-graphite" data-id="element-127" data-mp="g2l28t3s2zrd">{obs.label}</dt>
                  <dd className="font-mono text-[15px] text-ink" data-id="element-128" data-mp="26zfytoxiu2n">{obs.value}</dd>
                </div>)}
            </dl>

            <p className="mt-5 max-w-[74ch] text-sm leading-relaxed text-muted" data-id="element-129" data-mp="1wj7f6j8a9x48">
              Telemetry is descriptive evidence supporting this record, not a
              standalone score. More prompts is not better. More edits is not
              better. More checking is not better. Each figure is interpreted
              against the scenario it relates to.
            </p>
          </div>

          {/* Human reviewer conclusion */}
          <div className="border-t border-mist bg-bone px-7 py-7" data-id="element-130" data-mp="3utocxtha9m39">
            <h4 className={SECTION_LABEL} data-id="element-131" data-mp="3q26dj9uwrtke">Human reviewer conclusion</h4>

            <div className="mt-5 grid gap-1 sm:grid-cols-[16rem_1fr] sm:gap-6" data-id="element-132" data-mp="3q03e5z7v8o30">
              <span className={FIELD_LABEL} data-id="element-133" data-mp="8jlk0qs1ze9l">Overall evidence</span>
              <div className="flex flex-wrap gap-6" data-id="element-134" data-mp="3la9edtxozqb">
                {['Strong', 'Mixed', 'Limited'].map(option => <span key={option} className="flex items-center gap-2.5 text-[15px] text-ink" data-id="element-135" data-mp="hll2lnipuqen">
                    <span className="h-3.5 w-3.5 border border-ink/40 bg-white" aria-hidden="true" data-id="element-136" data-mp="cnjjx3p7ct3p" />
                    {option}
                  </span>)}
              </div>
            </div>

            <dl className="mt-5 divide-y divide-mist" data-id="element-137" data-mp="2eg7pvkt8dun6">
              {reviewerFields.map(field => <div key={field} className="grid items-baseline gap-1 py-3.5 sm:grid-cols-[16rem_1fr] sm:gap-6" data-id="element-138" data-mp="1hofe1wxuxvh5">
                  <dt className={FIELD_LABEL} data-id="element-139" data-mp="18nh99cflhjxi">{field}</dt>
                  <dd className="h-5 border-b border-dashed border-ink/25" data-id="element-140" data-mp="13ncubquc4x9e" />
                </div>)}
            </dl>

            <p className="mt-6 rounded-xl border border-ink/20 bg-white px-5 py-4 text-[15px] leading-relaxed text-ink" data-id="element-141" data-mp="20dvll47ymo72">
              <span className="font-semibold" data-id="element-142" data-mp="1wck27gfoiz7b">Human review required.</span> No
              decision is recorded until a named reviewer has examined the
              underlying evidence — not this summary — and signed off.
            </p>
          </div>

          <footer className="border-t border-mist px-7 py-6" data-id="element-143" data-mp="2k1crwv5pkath">
            <p className="max-w-[80ch] text-sm leading-relaxed text-muted" data-id="element-144" data-mp="2k3pghr5z1jfi">
              <span className="font-mono text-[11px] uppercase tracking-[0.1em] text-muted" data-id="element-145" data-mp="2fa4i3y0gunfs">
                What this record does not measure ·{' '}
              </span>
              This record evaluates observed performance within one assessment,
              at one library version, on one date. It is not a measure of
              overall ability, future performance or suitability for employment.
              Results are comparable within a requisition and library version;
              they are not comparable across versions or roles, and we do not
              present them as if they were.
            </p>
          </footer>
        </article>

        <aside className="rounded-2xl border border-mist bg-white/60 p-7" data-id="element-146" data-mp="3faycq98o4odv">
          <h3 className="font-display text-xl font-medium text-ink" data-id="element-147" data-mp="3fd2ikwq4nxgs">Evidence key</h3>
          <ul className="mt-6 space-y-4" data-id="element-148" data-mp="3kbvw2p5ezogy">
            {evidenceKey.map(row => <li key={row.label} className="flex items-baseline justify-between gap-3 border-b border-mist/70 pb-3 last:border-0 last:pb-0" data-id="element-149" data-mp="3oczd3ez77qjr">
                <span className="max-w-[24ch] text-[15px] leading-snug text-ink" data-id="element-150" data-mp="1t917vnnpb8c6">
                  {row.label}
                </span>
                <span className={`shrink-0 font-mono text-[11px] uppercase tracking-[0.1em] ${row.filled ? 'text-teal' : 'text-graphite'}`} data-id="element-151" data-mp="1p4tl3w0t02gl">
                  {row.credit}
                </span>
              </li>)}
          </ul>
          <p className="mt-7 border-t border-mist pt-6 text-[15px] leading-relaxed text-graphite" data-id="element-152" data-mp="27xe9b05k5je2">
            No points, no percentages, no composite. Categories are described in
            words because a number would imply a validation study we have not
            yet run.
          </p>
          <p className="mt-5 text-[15px] leading-relaxed text-graphite" data-id="element-153" data-mp="riw8gf93urck">
            Not assessed at all: polish, formatting, writing quality, speed. All
            free now, so all uninformative.
          </p>
        </aside>
      </div>

      <aside className="mt-6 rounded-2xl border border-clay/30 bg-clay/[0.05] p-8 lg:p-10" data-id="element-154" data-mp="2y86as56jvbqd">
        <h3 className="font-display text-2xl font-medium text-clay" data-id="element-155" data-mp="336hm6kchtuap">
          The record you'll actually need is for the candidate you didn't hire
        </h3>
        <p className="mt-4 max-w-[70ch] text-lg leading-relaxed text-ink" data-id="element-156" data-mp="2tges0gtmyaas">
          Nobody is asked to justify a hire in a formal setting. Rejections are
          where challenge and disclosure originate, so Lentic generates a full
          record for everyone who completes a session, not only those who
          advance.
        </p>
      </aside>
    </Band>;
}